import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { useDeleteComunicado } from '@/hooks/use-comunicado-mutations';
import type { ComunicadoListItem } from '../types-comunicados';

type Props = { item: ComunicadoListItem };

const ROW =
  'flex items-center justify-between gap-md border-b border-white/[0.04] py-sm last:border-b-0';
const TITLE = 'truncate text-sm font-medium text-text-primary';
const META = 'text-[11px] text-text-tertiary';
const PILL_PUB =
  'rounded-full bg-success/10 px-sm py-0.5 text-[10px] uppercase tracking-wide text-success';
const PILL_DRAFT =
  'rounded-full bg-white/[0.04] px-sm py-0.5 text-[10px] uppercase tracking-wide text-text-tertiary';
const ICON_BTN =
  'rounded-md p-1 text-text-tertiary transition-colors hover:bg-white/[0.04] hover:text-text-primary';

function fmtDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function RowActions({
  id,
  onDelete,
}: {
  id: string;
  onDelete: () => void;
}): ReactNode {
  const nav = useNavigate();
  return (
    <div className="flex items-center gap-xs">
      <button
        onClick={() => nav(`/admin/communications/${id}/edit`)}
        className={ICON_BTN}
        aria-label="Editar"
      >
        <Pencil size={14} />
      </button>
      <button onClick={onDelete} className={ICON_BTN} aria-label="Eliminar">
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function RowTitle({ item }: Props): ReactNode {
  const nav = useNavigate();
  return (
    <button
      onClick={() => nav(`/admin/communications/${item.id}/edit`)}
      className="flex min-w-0 flex-1 flex-col items-start gap-1 text-left"
    >
      <span className={TITLE}>{item.title}</span>
      <span className={META}>
        {item.is_published ? 'Publicado' : 'Borrador'} · {fmtDate(item.updated)}
      </span>
    </button>
  );
}

export function ComunicadosListRow({ item }: Props): ReactNode {
  const del = useDeleteComunicado();
  const onDelete = (): void => {
    if (!window.confirm('¿Eliminar este comunicado?')) return;
    del.mutate(item.id);
  };
  return (
    <li className={ROW}>
      <RowTitle item={item} />
      <span className={item.is_published ? PILL_PUB : PILL_DRAFT}>
        {item.is_published ? 'Live' : 'Draft'}
      </span>
      <RowActions id={item.id} onDelete={onDelete} />
    </li>
  );
}
